import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, RotateCcw, X } from 'lucide-react';
import { fetchOverrides, revertOverrides, saveOverrides } from '@/lib/premiumExams';
import { Hint, InfoDot } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';

// Phase 04 — per-student differences on top of an adopted class portion.
// The class blueprint is never edited; additions/removals are stored apart and
// the effective portion is always resolved server-side.
export function ExamOverridesDialog({ open, onOpenChange, exam, onSaved }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [addText, setAddText] = useState({});

  const load = useCallback(() => {
    if (!exam) return;
    setRows(null); setError('');
    fetchOverrides(exam.exam_id)
      .then(r => setRows(r.overrides || []))
      .catch(() => setError('We could not load the overrides. Please try again.'));
  }, [exam]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = (subject, key, value) =>
    setRows(prev => prev.map(r => (r.subject === subject ? { ...r, [key]: value } : r)));

  const toggleRemoval = (row, chapter) => {
    const next = row.removals.includes(chapter)
      ? row.removals.filter(c => c !== chapter)
      : [...row.removals, chapter];
    patch(row.subject, 'removals', next);
  };

  const addAddition = (row) => {
    const value = (addText[row.subject] || '').trim();
    if (!value) { toast.message('Type the chapter you want to add first'); return; }
    if (row.additions.includes(value)) { toast.message('Already added'); return; }
    patch(row.subject, 'additions', [...row.additions, value]);
    setAddText(t => ({ ...t, [row.subject]: '' }));
  };

  const save = async (row) => {
    setBusy(row.subject);
    try {
      const res = await saveOverrides(exam.exam_id, {
        subject: row.subject, additions: row.additions, removals: row.removals, notes: row.notes || '',
      });
      setRows(res.overrides || []);
      toast.success(`${row.subject} overrides saved`);
      onSaved?.();
    } catch (e) { toast.error(e?.message || 'Could not save the overrides'); }
    setBusy('');
  };

  const revert = async (row) => {
    setBusy(row.subject);
    try {
      const res = await revertOverrides(exam.exam_id, row.subject);
      setRows(res.overrides || []);
      toast.success(`${row.subject} is back to the class portion`);
      onSaved?.();
    } catch (e) { toast.error(e?.message || 'Could not revert'); }
    setBusy('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="exam-overrides-dialog">
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">Portion differences</DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          The class portion stays untouched. Anything added or dropped here applies only to {exam?.student_name || 'this'} study plan.
        </DialogDescription>

        {error ? (
          <p className="text-xs text-rose-600 font-body mt-2" data-testid="overrides-error">{error}</p>
        ) : !rows ? (
          <p className="flex items-center gap-2 text-xs text-slate-400 font-body mt-3" data-testid="overrides-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the class portion…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500 font-body mt-3" data-testid="overrides-empty">
            This exam has no subjects yet — add subjects first, then set the differences.
          </p>
        ) : (
          <div className="space-y-3 mt-3">
            {rows.map(row => (
              <div key={row.subject} className="rounded-2xl border border-slate-200 p-3" data-testid={`overrides-row-${row.subject}`}>
                <p className="font-heading text-[13px] font-semibold text-slate-900">{row.subject}</p>

                <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mt-2 inline-flex items-center gap-1">
                  Class portion — tap to drop
                  <InfoDot text={HELP.overrides.dropChip} side="right" testId={`hint-drop-${row.subject}`} />
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {row.class_portion.length === 0 && <span className="text-[11px] text-slate-400 font-body">Nothing in the class portion yet.</span>}
                  {row.class_portion.map(c => {
                    const dropped = row.removals.includes(c);
                    return (
                      <button key={c} data-testid={`overrides-drop-${row.subject}-${c}`} onClick={() => toggleRemoval(row, c)}
                        className={`text-[11px] font-body px-2 py-0.5 rounded-full border transition-colors ${dropped
                          ? 'border-rose-200 bg-rose-50 text-rose-500 line-through'
                          : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-rose-200'}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>

                <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mt-3 inline-flex items-center gap-1">
                  Extra for my child
                  <InfoDot text={HELP.overrides.addExtra} side="right" testId={`hint-add-${row.subject}`} />
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {row.additions.map(a => (
                    <span key={a} className={`text-[11px] font-body px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${(row.unmatched_additions || []).includes(a) ? 'bg-amber-50 border border-amber-300 text-amber-800' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
                      {a}
                      <button data-testid={`overrides-unadd-${row.subject}-${a}`} aria-label={`Remove ${a}`}
                        onClick={() => patch(row.subject, 'additions', row.additions.filter(x => x !== a))}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                {(row.unmatched_additions || []).length > 0 && (
                  <p className="text-[10px] text-amber-700 font-body mt-1" data-testid={`overrides-unmatched-${row.subject}`}>
                    {row.unmatched_additions.join(', ')} — we couldn't find {row.unmatched_additions.length > 1 ? 'these' : 'this'} in
                    the approved syllabus. We'll still plan {row.unmatched_additions.length > 1 ? 'them' : 'it'}, but check the spelling.
                  </p>
                )}
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Input data-testid={`overrides-add-input-${row.subject}`} value={addText[row.subject] || ''}
                    onChange={e => setAddText(t => ({ ...t, [row.subject]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAddition(row); } }}
                    placeholder="Extra chapter for this child" className="h-7 text-[11px] rounded-lg" />
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-shrink-0"
                    data-testid={`overrides-add-btn-${row.subject}`} onClick={() => addAddition(row)}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>

                <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mt-3 inline-flex items-center gap-1">
                  Why is this different?
                  <InfoDot text={HELP.overrides.notes} side="right" testId={`hint-notes-${row.subject}`} />
                </p>
                <Input data-testid={`overrides-notes-${row.subject}`} value={row.notes || ''}
                  onChange={e => patch(row.subject, 'notes', e.target.value)}
                  placeholder="Optional note for the study plan" className="h-7 mt-1 text-[11px] rounded-lg bg-slate-50" />

                <p className="text-[11px] font-body text-slate-500 mt-2" data-testid={`overrides-effective-${row.subject}`}>
                  <span className="font-heading text-slate-700">Effective portion:</span>{' '}
                  {row.effective_portion.length ? row.effective_portion.join(', ') : 'Nothing left — everything has been dropped.'}
                  <InfoDot text={HELP.overrides.effective} side="top" className="ml-1" testId={`hint-effective-${row.subject}`} />
                </p>

                <div className="flex items-center gap-2 mt-2">
                  <Hint text={HELP.overrides.save}>
                    <Button size="sm" className="h-7 text-[11px] bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
                      disabled={busy === row.subject} data-testid={`overrides-save-${row.subject}`} onClick={() => save(row)}>
                      {busy === row.subject ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
                    </Button>
                  </Hint>
                  <Hint text={HELP.overrides.revert}>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px]" disabled={busy === row.subject}
                      data-testid={`overrides-revert-${row.subject}`} onClick={() => revert(row)}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Back to class portion
                    </Button>
                  </Hint>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="overrides-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExamOverridesDialog;
